import { format, isToday, isYesterday } from 'date-fns';
import type {
  CrawlerSession,
  CrawlerSessionStats,
  CrawlerSessionStatusType,
  CrawlerSessionType,
  HistoryFilters,
} from '../../hooks/useCrawlerSessions';

export type CrawlerHistoryTriggerFilter = 'all' | 'MANUAL' | 'CRAWLER';

export type HistorySessionWithMeta = CrawlerSession & {
  stats: CrawlerSessionStats;
  credentialName: string;
  credentialRole?: string;
};

export type DayGroupedSessions = {
  dayKey: string;
  labelDate: Date;
  sessions: HistorySessionWithMeta[];
};

export type RelativeCrawlTime =
  | { kind: 'justNow' }
  | { kind: 'minutes'; count: number }
  | { kind: 'hours'; count: number }
  | { kind: 'days'; count: number }
  | { kind: 'absolute'; label: string };

export function isAutomatedCrawlerType(type: CrawlerSessionType): boolean {
  return type !== 'MANUAL';
}

export function toHistoryApiFilters({
  trigger,
  credentialIds,
  fromDate,
  toDate,
}: {
  trigger: CrawlerHistoryTriggerFilter;
  credentialIds: Array<string | null>;
  fromDate?: string;
  toDate?: string;
}): HistoryFilters {
  const filters: HistoryFilters = {};

  if (trigger === 'MANUAL' || trigger === 'CRAWLER') {
    filters.trigger = trigger;
  }

  if (credentialIds.length > 0) {
    filters.credentialIds = credentialIds;
  }

  if (fromDate) {
    filters.fromDate = fromDate;
  }

  if (toDate) {
    filters.toDate = toDate;
  }

  return filters;
}

export function formatRelativeCrawlTime({
  date,
  now = new Date(),
}: {
  date: Date | string | null | undefined;
  now?: Date;
}): RelativeCrawlTime | null {
  if (!date) {
    return null;
  }

  const parsed = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const diffMs = Math.max(0, now.getTime() - parsed.getTime());
  const seconds = Math.floor(diffMs / 1000);

  if (seconds < 60) {
    return { kind: 'justNow' };
  }

  const minutes = Math.floor(seconds / 60);

  if (minutes < 60) {
    return { kind: 'minutes', count: minutes };
  }

  const hours = Math.floor(minutes / 60);

  if (hours < 24) {
    return { kind: 'hours', count: hours };
  }

  const days = Math.floor(hours / 24);

  if (days < 30) {
    return { kind: 'days', count: days };
  }

  return { kind: 'absolute', label: format(parsed, 'MMM d') };
}

export function formatElapsedTimer({
  startedAt,
  now = new Date(),
}: {
  startedAt: string;
  now?: Date;
}): string {
  const start = new Date(startedAt);

  if (Number.isNaN(start.getTime())) {
    return '0:00';
  }

  const diffMs = Math.max(0, now.getTime() - start.getTime());
  const totalSeconds = Math.floor(diffMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export type CredentialLastSessionMeta = {
  finishedAt: string;
  crawlerType: CrawlerSessionType;
};

export function latestSessionMetaByCredential({
  sessions,
}: {
  sessions: Array<
    Pick<
      CrawlerSession,
      'credentialId' | 'finishedAt' | 'createdAt' | 'status' | 'crawlerType'
    >
  >;
}): Record<string, CredentialLastSessionMeta> {
  const result: Record<string, CredentialLastSessionMeta> = {};

  sessions.forEach((session) => {
    const key = session.credentialId ?? 'anonymous';
    const finishedAt = session.finishedAt ?? session.createdAt;

    if (!finishedAt) {
      return;
    }

    const previous = result[key];

    if (!previous || new Date(finishedAt) > new Date(previous.finishedAt)) {
      result[key] = {
        finishedAt,
        crawlerType: session.crawlerType,
      };
    }
  });

  return result;
}

export function mergeLastSessionMetaByCredential({
  sources,
}: {
  sources: Array<Record<string, CredentialLastSessionMeta>>;
}): Record<string, CredentialLastSessionMeta> {
  const merged: Record<string, CredentialLastSessionMeta> = {};

  for (const source of sources) {
    for (const [key, meta] of Object.entries(source)) {
      const current = merged[key];

      if (
        !current ||
        new Date(meta.finishedAt) > new Date(current.finishedAt)
      ) {
        merged[key] = meta;
      }
    }
  }

  return merged;
}

export type HistoryChangePart = {
  kind: 'endpoints' | 'pages';
  count: number;
};

export function formatChangeSummary({
  stats,
}: {
  stats: CrawlerSessionStats;
}): HistoryChangePart[] {
  const parts: HistoryChangePart[] = [];

  if (stats.endpointsNew > 0) {
    parts.push({ kind: 'endpoints', count: stats.endpointsNew });
  }

  const pagesNew = stats.pagesNew + stats.webpagesNew;

  if (pagesNew > 0) {
    parts.push({ kind: 'pages', count: pagesNew });
  }

  return parts;
}

export function historyStatusLabelKey(
  status: CrawlerSessionStatusType,
): 'completed' | 'stopped' | 'failed' | 'running' {
  if (status === 'COMPLETED') {
    return 'completed';
  }

  if (status === 'FAILED') {
    return 'failed';
  }

  if (status === 'ABORTED' || status === 'ABORTING') {
    return 'stopped';
  }

  return 'running';
}

export function triggerLabelKey(
  crawlerType: CrawlerSessionType,
): 'manual' | 'crawler' {
  return crawlerType === 'MANUAL' ? 'manual' : 'crawler';
}

export function formatDayGroupLabel(date: Date): string {
  if (isToday(date)) {
    return 'today';
  }

  if (isYesterday(date)) {
    return 'yesterday';
  }

  return format(date, 'MMM d, yyyy');
}

export function groupSessionsByDay(
  sessions: HistorySessionWithMeta[],
): DayGroupedSessions[] {
  const groups: DayGroupedSessions[] = [];
  const groupMap = new Map<string, DayGroupedSessions>();

  sessions.forEach((session) => {
    const date = new Date(session.createdAt);
    const dayKey = format(date, 'yyyy-MM-dd');
    let group = groupMap.get(dayKey);

    if (!group) {
      group = {
        dayKey,
        labelDate: date,
        sessions: [],
      };
      groupMap.set(dayKey, group);
      groups.push(group);
    }

    group.sessions.push(session);
  });

  return groups.sort((a, b) => b.labelDate.getTime() - a.labelDate.getTime());
}

export function oldestCreatedAt(dates: string[]): string | undefined {
  if (dates.length === 0) {
    return undefined;
  }

  return dates.reduce((oldest, current) =>
    new Date(current).getTime() < new Date(oldest).getTime() ? current : oldest,
  );
}
